import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiGet, apiSend } from "@/lib/api-client";
import { logger } from "@/lib/logger";
import { announcementKeys } from "@/lib/query";
import { type Announcement, announcementArraySchema } from "@/schemas/announcements";

// Refetch the active list every 5 minutes so a banner pushed mid-session
// appears without a page reload. Cheap: the response is small and the
// endpoint is a fast lookup with at most a handful of rows.
const ACTIVE_REFETCH_MS = 5 * 60 * 1000;

// Active (non-dismissed, in window) announcements for the current user.
// Returns an empty array on error / loading — the banner just hides
// itself when there's nothing to show; failures here should not break
// the rest of the page.
export function useActiveAnnouncements() {
  const { data } = useQuery<Announcement[]>({
    queryKey: announcementKeys.active,
    queryFn: () => apiGet("/api/announcements", announcementArraySchema),
    refetchInterval: ACTIVE_REFETCH_MS,
    staleTime: 60_000,
  });
  return data ?? [];
}

// Dismiss an announcement for the current user. Optimistic — removes
// the banner from the cached list immediately, then syncs to the
// server. On failure we put it back and surface a logged error.
export function useDismissAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiSend("POST", `/api/announcements/${id}/dismiss`, undefined, z.unknown()),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: announcementKeys.active });
      const previous = queryClient.getQueryData<Announcement[]>(announcementKeys.active);
      if (previous) {
        queryClient.setQueryData<Announcement[]>(
          announcementKeys.active,
          previous.filter((a) => a.id !== id),
        );
      }
      return { previous };
    },
    onError: (error, _id, ctx) => {
      logger.error("announcement_dismiss_failed", { error });
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(announcementKeys.active, ctx.previous);
      }
    },
  });
}
